import { Button } from 'admin/components/atoms/Buttons';
import Headings from 'admin/components/atoms/Headings';
import Loader from 'admin/components/atoms/Loader';
import Table from 'admin/components/atoms/Table';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import Pager from 'admin/components/shared/Pager';
import useOAuthClients from 'admin/hooks/useOAuthClients';
import { formatDate } from 'admin/utils';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { fetchOrganizations } from 'admin/actions/organization';
import Select, { addNullOption } from 'admin/components/atoms/Select';
import { allowedGrantTypeOptions } from './constants';

type OrgOption = { value: string | null; label: string };

const FormWrapper = styled.div`
  ${Select} {
    width: 500px;
  }
  ${Button} {
    margin-bottom: 10px;
  }
`;

const SearchResult = styled.span`
  display: inline-block;
  margin-left: 1rem;
`;

const LoaderWrapper = styled.div`
  background: #f5f5f5;
  text-align: center;
  padding: 1rem;
`;

const OAuthClients = () => {
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'OAuthClient', href: '', isCurrentPage: true },
  ];

  const [isLoading, oauthClients, pagination, count, error, getOAuthClients] = useOAuthClients();
  const [organizationOptions, setOrganizationOptions] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);

  useEffect(() => {
    fetchOrganizations('per_page=10000')
      .then((data) => {
        const orgs = (data.rows ?? []).map((org) => ({
          value: org.code ?? '',
          label: org.name ?? '',
        }));
        setOrganizationOptions(addNullOption(orgs, 'すべての組織'));
      })
      .catch((err) => {
        console.error('fetchOrganizations failed:', err);
      });
  }, []);

  const handleChangePage = (page: number) => {
    getOAuthClients({ page, organization_code: selectedOrg ? selectedOrg.value : null });
  };

  const handleChangeOrganization = (organization: OrgOption | null) => {
    setSelectedOrg(organization);
    getOAuthClients({ page: 1, organization_code: organization ? organization.value : null });
  };

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />
      {error.message ? <ApiResults messages={[{ text: error.message, isError: true }]} /> : null}
      <Link to="/service-settings/oauth-client/create">
        <Button>OAuthClient登録</Button>
      </Link>
      {isLoading ? (
        <LoaderWrapper>
          <Loader type="Oval" height={50} width={50} />
        </LoaderWrapper>
      ) : (
        <div>
          <div>
            <Select
              options={organizationOptions}
              value={selectedOrg}
              onChange={handleChangeOrganization}
              placeholder="組織を選択してください"
            />
          </div>
          <Headings.h5 style={{ marginTop: '16px' }}>検索結果</Headings.h5>
          <SearchResult> 件数：{count}</SearchResult>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Cell size="3" align="left">
                  ID
                </Table.Cell>
                <Table.Cell size="1.75" align="left">
                  OAuthClient名
                </Table.Cell>
                <Table.Cell size="1.75" align="left">
                  組織名
                </Table.Cell>
                <Table.Cell size="2" align="left">
                  許可種別
                </Table.Cell>
                <Table.Cell size="1.5" align="left">
                  デフォルトスコープ
                </Table.Cell>
                <Table.Cell size="1" align="left">
                  作成日時
                </Table.Cell>
                <Table.Cell size="1" align="left">
                  更新日時
                </Table.Cell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {oauthClients.map((oauthClient) => (
                <Table.Row key={oauthClient.id}>
                  <Table.Cell align="left">
                    <Link to={`/service-settings/oauth-client/${oauthClient.id}`}>
                      {oauthClient.id}
                    </Link>
                  </Table.Cell>
                  <Table.Cell align="left">{oauthClient.name}</Table.Cell>
                  <Table.Cell align="left">{oauthClient.organization.name}</Table.Cell>
                  <Table.Cell align="left">
                    {oauthClient.allowed_grant_types.map((type) => {
                      const option = allowedGrantTypeOptions.find((o) => o.value === type);
                      return option ? <div key={type}>{option.labelText}</div> : null;
                    })}
                  </Table.Cell>
                  <Table.Cell align="left">
                    {oauthClient.default_scopes.map((scope) => {
                      if (scope === 'private_money:read') {
                        return <div>マネー（Read）</div>;
                      } else if (scope === 'private_money:write') {
                        return <div>マネー（Write）</div>;
                      }
                      return null;
                    })}
                  </Table.Cell>
                  <Table.Cell align="left">{formatDate(oauthClient.created_at)}</Table.Cell>
                  <Table.Cell align="left">{formatDate(oauthClient.updated_at)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row noBorder>
                <Table.Cell colSpan={7} align="center" styles={{ paddingTop: '16px' }}>
                  <Pager pagination={pagination} onChangePage={handleChangePage} />
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </div>
      )}
    </FormWrapper>
  );
};

export default OAuthClients;
